/** The activity category record's section registry (plan §4 "One card", §9.5 "Spec enums
 *  for the VIEW halves", §10.3; O8): every key the record shows → its card (the one card,
 *  Category), its position in it, its block (the Editable fields, or the System block of the
 *  derived activity count), its label and its widget.
 *
 *  Labels, kinds and widgets mirror the backend's catalog (`apiv2/lib/console/
 *  activity_category_changeset/fields.py`, its committed snapshot `apiv2/console-catalogs/
 *  activity_category.json`): the page renders from the detail alone before the catalog is
 *  read, and the widget names the edit-mode input the catalog names (the records core's
 *  through `coreWidget`), so nothing moves when the catalog takes over.
 *
 *  The same shape as the activity, booking, order and user registries (`RegistryEntry`), so
 *  the records core lays all of them out alike; a category has no lifecycle, so `stage` and
 *  `facet` are the card.
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `ActivityCategoryRecordFieldKey`, in its order; the editable ones exactly
 *  `ActivityCategoryRecordEditableFieldKey`; the cards, kinds and widgets those of the
 *  spec's `ActivityCategoryRecordCatalogField`; and per key the label, kind, card, widget and
 *  choices of the server's catalog. The map is typed by the generated enum as
 *  well, so a regenerated `types.ts` that adds or drops a key fails the typecheck before the
 *  test runs. */
import {
  ACTIVITY_CATEGORY_RECORD_SECTIONS,
  type ActivityCategoryRecordCatalogField,
  type ActivityCategoryRecordFieldKey,
  type ActivityCategoryRecordFieldKind,
  type ActivityCategoryRecordSection,
  type ActivityCategoryRecordWidget,
} from '@/api/activityCategoryRecord';
import { catalogRecordField, coreWidget } from '@/features/catalog/recordFields';
import type { RecordField } from '@/features/records/editor/types';
import type { FieldBlockKind, RegistryEntry } from '@/features/records/views/types';

/** The category changes' `entity`. */
export const ACTIVITY_CATEGORY_ENTITY = 'activity_category';

export interface ActivityCategoryRegistryEntry
  extends RegistryEntry<ActivityCategoryRecordSection, ActivityCategoryRecordSection> {
  label: string;
  /** `editable` — an input in edit mode; `derived` — system, read-only. */
  kind: ActivityCategoryRecordFieldKind;
  /** The edit-mode input; null for the read-only kind. */
  widget: ActivityCategoryRecordWidget;
}

const BLOCK_OF: Readonly<Record<ActivityCategoryRecordFieldKind, FieldBlockKind>> = {
  editable: 'editable',
  derived: 'system',
};

function entry(
  section: ActivityCategoryRecordSection,
  order: number,
  kind: ActivityCategoryRecordFieldKind,
  label: string,
  widget: ActivityCategoryRecordWidget = null,
): ActivityCategoryRegistryEntry {
  return { stage: section, facet: section, order, block: BLOCK_OF[kind], label, kind, widget };
}

const edit = (
  section: ActivityCategoryRecordSection,
  order: number,
  label: string,
  widget: NonNullable<ActivityCategoryRecordWidget>,
) => entry(section, order, 'editable', label, widget);
const system = (section: ActivityCategoryRecordSection, order: number, label: string) =>
  entry(section, order, 'derived', label);

export const ACTIVITY_CATEGORY_RECORD_REGISTRY = {
  title: edit('category', 10, 'Title', 'text'),
  icon: edit('category', 20, 'Icon', 'image'),
  atag: edit('category', 30, 'Access tags', 'atags'),
  activity_count: system('category', 40, 'Activities'),
} as const satisfies Record<ActivityCategoryRecordFieldKey, ActivityCategoryRegistryEntry>;

export type ActivityCategoryRegistry = Readonly<Record<ActivityCategoryRecordFieldKey, ActivityCategoryRegistryEntry>>;

/** Card titles, in page order. */
export const ACTIVITY_CATEGORY_SECTION_TITLES: Readonly<Record<ActivityCategoryRecordSection, string>> = {
  category: 'Category',
};

/** The keys grouped by card, ordered by card then position. Pure. */
export function activityCategoryPlacements(
  registry: ActivityCategoryRegistry = ACTIVITY_CATEGORY_RECORD_REGISTRY,
): Map<ActivityCategoryRecordSection, ActivityCategoryRecordFieldKey[]> {
  const index = (s: ActivityCategoryRecordSection) => ACTIVITY_CATEGORY_RECORD_SECTIONS.indexOf(s);
  const keys = (Object.keys(registry) as ActivityCategoryRecordFieldKey[]).sort((a, b) => {
    const x = registry[a];
    const y = registry[b];
    return index(x.stage) - index(y.stage) || x.order - y.order;
  });
  const out = new Map<ActivityCategoryRecordSection, ActivityCategoryRecordFieldKey[]>();
  for (const key of keys) {
    const section = registry[key].stage;
    const list = out.get(section);
    if (list) list.push(key);
    else out.set(section, [key]);
  }
  return out;
}

/** The registry as the records core's catalog (`RecordEditorConfig.fields`), in card order:
 *  what the FieldSlots read until the server's edit catalog arrives — the same labels, kinds
 *  and inputs (`coreWidget`: the access tags are the core's `tags`) as the catalog's (the
 *  contract test), so nothing moves when it takes over. */
export function activityCategoryRecordFields(
  registry: ActivityCategoryRegistry = ACTIVITY_CATEGORY_RECORD_REGISTRY,
): RecordField[] {
  return [...activityCategoryPlacements(registry).values()].flat().map((key) => {
    const e = registry[key];
    return {
      key,
      entity: ACTIVITY_CATEGORY_ENTITY,
      label: e.label,
      kind: e.kind,
      widget: coreWidget(e.widget),
      choices: [],
      guards: [],
      permissions: [],
      follows_lead: false,
      effects: [],
      automatic: [],
      restech_rule: null,
    };
  });
}

/** A field of the server's catalog as the records core's `RecordField` — the catalog
 *  records' shared reading (`catalogRecordField`); no category field is an enum, so the
 *  spec types its choices as none. */
export function activityCategoryCatalogField(f: ActivityCategoryRecordCatalogField): RecordField {
  return catalogRecordField({ ...f, choices: [] });
}
